import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster, toast } from "sonner";

import { LoginPage } from "./components/auth/LoginPage";
import { AppErrorBoundary } from "./components/common/AppErrorBoundary";
import { RequirePermission } from "./components/common/RequirePermission";
import { AppShell } from "./components/layout/AppShell";
import { fetchCurrentUser } from "./lib/api";
import { AUTH_LOGOUT_EVENT, scheduleProactiveRefresh, stopProactiveRefresh } from "./lib/authRefresh";
import { clearSession, getSessionToken, saveSession } from "./lib/session";
import { syncEngine } from "./lib/sync/syncEngine";
import { CustomerDetailPage } from "./pages/CustomerDetailPage";
import { CustomersPage } from "./pages/CustomersPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DispatchBoardPage } from "./pages/DispatchBoardPage";
import { DriversPage } from "./pages/DriversPage";
import AuditTrailPage from "./pages/AuditTrailPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import InvoicePrintPage from "./pages/InvoicePrintPage";
import LoadImportPage from "./pages/LoadImportPage";
import { LoadsPage } from "./pages/LoadsPage";
import { PlanningBoardPage } from "./pages/PlanningBoardPage";
import RecordsPage from "./pages/RecordsPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { ReportsPage } from "./pages/ReportsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TrailersPage } from "./pages/TrailersPage";
import { TrucksPage } from "./pages/TrucksPage";
import UsersPage from "./pages/UsersPage";

const LoadingScreen = () => (
  <div
    className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.95),_rgba(240,240,240,0.8))] px-6"
    data-testid="app-loading-screen"
  >
    <div className="panel-surface flex max-w-md items-center gap-4 px-6 py-5">
      <div className="h-3 w-3 animate-pulse rounded-full bg-zinc-900" />
      <div>
        <p className="font-display text-lg text-zinc-900">Preparing your workspace</p>
        <p className="text-sm text-zinc-500">Loading secure session details and live dispatch records.</p>
      </div>
    </div>
  </div>
);

const ProtectedLayout = ({ user, onLogout }) => (
  <AppShell user={user} onLogout={onLogout}>
    <Outlet />
  </AppShell>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bootstrap = async () => {
      const token = getSessionToken();
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const currentUser = await fetchCurrentUser();
        setUser(currentUser);
        scheduleProactiveRefresh();
      } catch (error) {
        if (!getSessionToken()) {
          return;
        }
        if (error?.response?.status && error.response.status < 500) {
          clearSession();
          toast.error("Your previous session expired. Please sign in again.");
        } else {
          toast.error("Could not reach the server. Check your connection and try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    const handleForcedLogout = () => {
      syncEngine.stop();
      setUser(null);
      toast.error("Your session expired. Please sign in again.");
    };
    window.addEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);
  }, []);

  useEffect(() => {
    if (user) {
      syncEngine.setCurrentUser(user);
      syncEngine.start();
    } else {
      syncEngine.stop();
    }
  }, [user]);

  const handleAuthenticated = (payload) => {
    saveSession(payload);
    scheduleProactiveRefresh();
    setUser(payload.user);
  };

  const handleLogout = () => {
    syncEngine.stop();
    syncEngine.clearLocalData();
    stopProactiveRefresh();
    clearSession();
    setUser(null);
    toast.success("You have been logged out.");
  };

  return (
    <div className="App">
      {loading ? <LoadingScreen /> : (
      <AppErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={user ? <Navigate replace to="/dashboard" /> : <LoginPage onAuthenticated={handleAuthenticated} />}
            />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              element={user ? <ProtectedLayout user={user} onLogout={handleLogout} /> : <Navigate replace to="/login" />}
            >
              <Route path="/dashboard" element={<RequirePermission permission="dashboard" user={user}><DashboardPage /></RequirePermission>} />
              <Route path="/customers" element={<RequirePermission permission="customers" user={user}><CustomersPage /></RequirePermission>} />
              <Route path="/customers/:customerId" element={<RequirePermission permission="customers" user={user}><CustomerDetailPage /></RequirePermission>} />
              <Route path="/records" element={<RequirePermission permission="records" user={user}><RecordsPage /></RequirePermission>} />
              <Route path="/trucks" element={<RequirePermission permission="records" user={user}><TrucksPage /></RequirePermission>} />
              <Route path="/trailers" element={<RequirePermission permission="records" user={user}><TrailersPage /></RequirePermission>} />
              <Route path="/drivers" element={<RequirePermission permission="records" user={user}><DriversPage /></RequirePermission>} />
              <Route path="/loads" element={<RequirePermission permission="loads" user={user}><LoadsPage /></RequirePermission>} />
              <Route path="/loads/import" element={<RequirePermission permission="loads" user={user}><LoadImportPage /></RequirePermission>} />
              <Route path="/dispatch-board" element={<RequirePermission permission="dispatch_board" user={user}><DispatchBoardPage /></RequirePermission>} />
              <Route path="/planning-board" element={<RequirePermission permission="planning_board" user={user}><PlanningBoardPage /></RequirePermission>} />
              <Route path="/invoices" element={<RequirePermission permission="invoices" user={user}><InvoicesPage /></RequirePermission>} />
              <Route path="/invoices/print" element={<RequirePermission permission="invoices" user={user}><InvoicePrintPage /></RequirePermission>} />
              <Route path="/audit-trail" element={<AuditTrailPage />} />
              <Route path="/reports" element={<RequirePermission permission="reports" user={user}><ReportsPage /></RequirePermission>} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/settings" element={<RequirePermission permission="settings" user={user}><SettingsPage /></RequirePermission>} />
            </Route>
            <Route path="*" element={<Navigate replace to={user ? "/dashboard" : "/login"} />} />
          </Routes>
        </BrowserRouter>
      </AppErrorBoundary>
      )}
      <Toaster closeButton richColors position="bottom-right" />
    </div>
  );
}

export default App;
